import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

const PaymentUpdate = () => {
  const [bookingId, setBookingId] = useState('');
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [paymentData, setPaymentData] = useState([]);

  // Sample payment data that will be loaded after search
  const samplePaymentData = [
    {
      id: 1,
      paymentDate: '2024-10-01',
      paidAmount: 50000,
      receiptNo: 'RCP001',
      paymentMode: 'Cheque',
      chqDdDate: '2024-10-01',
      chqDdNo: 'CHQ123456',
      paymentType: 'Booking',
      chqBounceCharges: 0,
      paymentStatus: 'Cleared',
      receivedBy: 'John Doe',
    },
    {
      id: 2,
      paymentDate: '2024-10-15',
      paidAmount: 75000,
      receiptNo: 'RCP002',
      paymentMode: 'DD',
      chqDdDate: '2024-10-15',
      chqDdNo: 'DD789012',
      paymentType: 'Installment',
      chqBounceCharges: 0,
      paymentStatus: 'Cleared',
      receivedBy: 'Jane Smith',
    },
    {
      id: 3,
      paymentDate: '2024-10-20',
      paidAmount: 25000,
      receiptNo: 'RCP003',
      paymentMode: 'Cheque',
      chqDdDate: '2024-10-20',
      chqDdNo: 'CHQ345678',
      paymentType: 'PLC',
      chqBounceCharges: 500,
      paymentStatus: 'Bounced',
      receivedBy: 'Mike Johnson',
    },
  ];

  const paymentModeOptions = ['Cash', 'Cheque', 'DD', 'NEFT', 'RTGS', 'UPI', 'Card'];
  const paymentTypeOptions = ['Booking', 'Installment', 'PLC', 'Registration', 'Other'];
  const paymentStatusOptions = ['Pending', 'Cleared', 'Bounced', 'Cancelled'];

  const handleSearch = () => {
    if (bookingId.trim()) {
      setPaymentData([...samplePaymentData]);
      setShowPaymentDetails(true);
    } else {
      alert('Please enter a Booking ID');
    }
  };

  const handleInputChange = (id, field, value) => {
    setPaymentData(prevData =>
      prevData.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handleDelete = (id) => {
    setPaymentData(prevData => prevData.filter(item => item.id !== id));
  };

  const handleUpdate = () => {
    console.log('Updating payment data:', paymentData);
    alert('Payment details updated successfully!');
  };

  const handleReset = () => {
    setPaymentData([...samplePaymentData]);
    alert('Payment details reset to original values!');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
        Update Payment
      </Typography>

      {/* Search Section */}
      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
          Search Payment Details
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Booking ID"
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              size="small"
              placeholder="Enter Booking ID to search payments"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              size="small"
              sx={{ py: 0.5, fontSize: '0.8rem', width: 'auto', minWidth: '100px' }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Payment Details Table */}
      {showPaymentDetails && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Payment Details for Booking ID: {bookingId}
          </Typography>

          {paymentData.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No payment records found for this booking ID.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Payment Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Paid Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Receipt No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Payment Mode</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>CHQ/DD Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>CHQ/DD No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Payment Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>CHQ Bounce Charges</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Payment Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Received By</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentData.map((row, index) => (
                      <TableRow 
                        key={row.id}
                        sx={{ 
                          '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                          '&:hover': { backgroundColor: '#e3f2fd' }
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            value={row.paymentDate}
                            onChange={(e) => handleInputChange(row.id, 'paymentDate', e.target.value)}
                            size="small"
                            sx={{ minWidth: '140px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={row.paidAmount}
                            onChange={(e) => handleInputChange(row.id, 'paidAmount', Number(e.target.value))}
                            size="small"
                            sx={{ minWidth: '120px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={row.receiptNo}
                            onChange={(e) => handleInputChange(row.id, 'receiptNo', e.target.value)}
                            size="small"
                            sx={{ minWidth: '100px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: '120px' }}>
                            <Select
                              value={row.paymentMode}
                              onChange={(e) => handleInputChange(row.id, 'paymentMode', e.target.value)}
                            >
                              {paymentModeOptions.map((mode) => (
                                <MenuItem key={mode} value={mode}>
                                  {mode}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            value={row.chqDdDate}
                            onChange={(e) => handleInputChange(row.id, 'chqDdDate', e.target.value)}
                            size="small"
                            sx={{ minWidth: '140px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={row.chqDdNo}
                            onChange={(e) => handleInputChange(row.id, 'chqDdNo', e.target.value)}
                            size="small"
                            sx={{ minWidth: '120px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: '120px' }}>
                            <Select
                              value={row.paymentType}
                              onChange={(e) => handleInputChange(row.id, 'paymentType', e.target.value)}
                            >
                              {paymentTypeOptions.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {type}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={row.chqBounceCharges}
                            onChange={(e) => handleInputChange(row.id, 'chqBounceCharges', Number(e.target.value))}
                            size="small"
                            sx={{ minWidth: '120px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: '120px' }}>
                            <Select
                              value={row.paymentStatus}
                              onChange={(e) => handleInputChange(row.id, 'paymentStatus', e.target.value)}
                            >
                              {paymentStatusOptions.map((status) => (
                                <MenuItem key={status} value={status}>
                                  {status}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={row.receivedBy}
                            onChange={(e) => handleInputChange(row.id, 'receivedBy', e.target.value)}
                            size="small"
                            sx={{ minWidth: '120px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDelete(row.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  sx={{ px: 3 }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleUpdate}
                  sx={{ px: 3 }}
                >
                  Update
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default PaymentUpdate;